import type { LanguageType } from './utils/types';

import { Box, Text } from '@rango-dev/whale';
import React, { useEffect } from 'react';
import { getI18n } from 'react-i18next';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import styles from './BaseRoutes.module.css';
import { AffiliateFallback } from './components/Affiliate/AffiliateFallback';
import { LogIn } from './components/Profile';
import {
  DEFAULT_LANGUAGE,
  EXPIRATION_LOCALE_COOKIE_DAYS,
  Languages,
  LOCALE_COOKIE,
} from './constants/i18n';
import { WIDGET_BASE_ROUTE } from './constants/navigation';
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import { Leaderboard } from './pages/Leaderboard';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile/Profile';
import { Swap } from './pages/Swap';
import { updateLanguage } from './state/SettingsSlice';
import { updateWidgetConfig } from './state/WidgetSlice';
import { getCookie, isValidLocale, retry, setCookie } from './utils/generalFunctions/common';

type RouteType = { path: string; element: React.ReactNode };

const Affiliate = React.lazy(async () => retry(async () => import('./pages/Affiliate')));
const Portfolio = React.lazy(async () =>
  retry(async () => {
    const portfolioModule = await import('./pages/Portfolio');
    return { default: portfolioModule.Portfolio };
  }),
);
const NavigateToWidgetBase = () => {
  const [searchParams] = useSearchParams();

  return (
    <Navigate
      to={{
        pathname: WIDGET_BASE_ROUTE,
        search: searchParams.toString(),
      }}
      replace
    />
  );
};

const LocaleHandler = () => {
  const dispatch = useAppDispatch();
  const { locale } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const userLocale = getCookie(LOCALE_COOKIE);

  if (locale && userLocale !== locale) {
    setCookie(LOCALE_COOKIE, locale, EXPIRATION_LOCALE_COOKIE_DAYS);
  }

  // Replace /en/... with the neutral route
  if (locale === DEFAULT_LANGUAGE) {
    const neutralPath = pathname.replace(`/${DEFAULT_LANGUAGE}`, '') || '/';

    // Navigate to the neutral path with the current search parameters
    navigate(
      {
        pathname: neutralPath,
        search: search,
      },
      { replace: true },
    );
  }

  useEffect(() => {
    if (!locale) {
      return;
    }
    if (isValidLocale(locale) && locale !== getI18n().language) {
      dispatch(updateLanguage((locale as LanguageType['value']) || 'en'));
      dispatch(updateWidgetConfig({ language: locale as LanguageType['value'] }));
    }
  }, []);

  if (locale && !isValidLocale(locale)) {
    return <NotFound />;
  }

  return <Outlet />;
};

const routes: RouteType[] = [
  {
    path: '/health',
    element: (
      <React.Suspense fallback={<div />}>
        <Box position="fixed" top="50%" left="50%" className={styles.healthWrapper}>
          <Text size="5" weight="medium">
            The App is Healthy.
          </Text>
        </Box>
      </React.Suspense>
    ),
  },
  {
    path: '/affiliate',
    element: (
      <React.Suspense fallback={<AffiliateFallback />}>
        <Affiliate />
      </React.Suspense>
    ),
  },
  {
    path: '/portfolio',
    element: (
      <React.Suspense fallback={<div />}>
        <Portfolio />
      </React.Suspense>
    ),
  },
  { path: '/profile/:username', element: <Profile /> },
  {
    path: '/profile',
    element: <LogIn />,
  },
  {
    path: '/leaderboard',
    element: <Leaderboard />,
  },
  {
    path: '/leaderboard/:type',
    element: <Leaderboard />,
  },
  {
    path: `${WIDGET_BASE_ROUTE}/*`,
    element: <Swap />,
  },
  {
    path: '/refuel/*',
    element: <Swap />,
  },
  {
    path: '/swap/*',
    element: <Swap />,
  },
];

export function BaseRoutes() {
  const { language: currentLanguage } = useAppSelector((state) => state.settings);

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!currentLanguage) {
      return;
    }
    const pathSegments = pathname.split('/').filter(Boolean);
    const supportedLanguages = Languages.map((lang) => lang.value);
    const firstSegment = pathSegments[0] as LanguageType['value'];
    const isFirstSegmentLang = supportedLanguages.includes(firstSegment);
    const isDefaultLang = currentLanguage === DEFAULT_LANGUAGE;

    const basePath = isFirstSegmentLang ? pathname.replace(`/${firstSegment}`, '') : pathname;
    if (isDefaultLang && isFirstSegmentLang) {
      navigate({ pathname: basePath, search }, { replace: true });
      return;
    }
    const needsPrefixUpdate = !isDefaultLang && firstSegment !== currentLanguage;
    if (needsPrefixUpdate) {
      const newPathname = `/${currentLanguage}${basePath}`;
      navigate({ pathname: newPathname, search }, { replace: true });
    }
  }, [pathname, search, currentLanguage]);

  return (
    <Routes>
      {/* Routes without locale */}
      <Route path="/*" element={<LocaleHandler />}>
        <Route index element={<NavigateToWidgetBase />} />
        {routes.map((route) => (
          <Route key={route.path} path={route.path.slice(1)} element={route.element} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Routes with locale */}
      <Route path="/:locale/*" element={<LocaleHandler />}>
        <Route index element={<NavigateToWidgetBase />} />
        {routes.map((route) => (
          <Route key={route.path} path={route.path.slice(1)} element={route.element} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
